<script setup lang="ts">
// types
import type { SectionReasonsListProps } from '@ts-sections/section-reasons-list/interfaces'
// hooks
import { useSectionReasonsList } from '@hooks/sections/section-reasons-list.hook'
// helpers
import { STRAPI_IMAGE_SIZES } from '@helpers/strapi-media'
// components
import { CardIcon } from '@UI/Cards/CardIcon'
import { AppImage } from '@UI/Images/AppImage'
import { SectionLayout } from '@UI/Layouts/SectionLayout'

defineOptions({
  name: 'SectionReasonsList',
})

const props = defineProps<{
  section: SectionReasonsListProps
}>()

const {
  refTitle,
  refDescription,
  refBackgroundColor,
  getImageAlt,
} = useSectionReasonsList({
  section: () => props.section,
})
</script>

<template>
  <SectionLayout
    class="section-reasons-list"
    :data-component="section.__component"
    :title="refTitle"
    :title-tag="section.layout?.titleTag"
    :description="refDescription"
    :color="refBackgroundColor"
    :space="section.layout?.space"
  >
    <ul class="section-reasons-list__grid">
      <template
        v-for="(reason, index) in section.reasons"
        :key="reason.id ?? index"
      >
        <li
          class="section-reasons-list__cell section-reasons-list__cell--card"
          :class="`section-reasons-list__cell--card-${index + 1}`"
        >
          <CardIcon
            class="section-reasons-list__card"
            layout="vertical"
            surface="white"
            :title="reason.title"
            :description="reason.description"
          />
        </li>
        <li
          class="section-reasons-list__cell section-reasons-list__cell--image"
          :class="[
            `section-reasons-list__cell--image-${index + 1}`,
            `section-reasons-list__cell--${reason.color ?? 'ultraviolet'}`,
          ]"
        >
          <AppImage
            v-if="reason.image"
            class="section-reasons-list__illustration"
            :media="reason.image"
            :sizes="STRAPI_IMAGE_SIZES"
            :alt="getImageAlt(reason)"
          />
        </li>
      </template>
    </ul>
  </SectionLayout>
</template>

<style scoped lang="scss" src="@scss/components/Sections/SectionReasonsList.scss" />
